import { useCallback } from "react";
import {
  type EventTransformerHooks,
  useEventTracking,
  type UseInstantTransformEvent,
} from "@b2bportal/core-tracking";
import {
  type FlightsPriceFreezeHTSEvent,
  FlightsPriceFreezePurchaseTrackingEvents,
  FLIGHTS_PRICE_FREEZE_PRODUCT_TYPE,
} from "../events";
import { getTransformedEventProperties } from "../transformedEventProperties";
import {
  FlightsPriceFreezePurchaseRawEventName,
  type FlightsPriceFreezeRawEvent,
} from "../types";

const useFlightsPriceFreezeEntryLoadedTransformer: UseInstantTransformEvent<
  FlightsPriceFreezeRawEvent,
  FlightsPriceFreezeHTSEvent
> = () => {
  return useCallback((event) => {
    return {
      name: FlightsPriceFreezePurchaseTrackingEvents.PRICE_FREEZE_VIEWED_CTA,
      properties: {
        product_type: FLIGHTS_PRICE_FREEZE_PRODUCT_TYPE,
        ...event.properties,
      },
    };
  }, []);
};

const useFlightsPriceFreezeEntryClickedTransformer: UseInstantTransformEvent<
  FlightsPriceFreezeRawEvent,
  FlightsPriceFreezeHTSEvent
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(() => {
    const eventProperties = getTransformedEventProperties({
      trackingAttributes: getAllAttributes(),
    });

    return {
      name: FlightsPriceFreezePurchaseTrackingEvents.PRICE_FREEZE_ENTRY_SELECTED,
      properties: eventProperties.flightPriceFreezeProperties,
    };
  }, [getAllAttributes]);
};

const useFlightsPriceFreezeDetailsLoadedTransformer: UseInstantTransformEvent<
  FlightsPriceFreezeRawEvent,
  FlightsPriceFreezeHTSEvent
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(() => {
    const eventProperties = getTransformedEventProperties({
      trackingAttributes: getAllAttributes(),
    });

    return {
      name: FlightsPriceFreezePurchaseTrackingEvents.PRICE_FREEZE_VIEWED_MISSION_CONTROL,
      properties: eventProperties.flightPriceFreezeProperties,
    };
  }, [getAllAttributes]);
};

const useFlightsPriceFreezeDurationChangedTransformer: UseInstantTransformEvent<
  FlightsPriceFreezeRawEvent,
  FlightsPriceFreezeHTSEvent
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(
    (event) => {
      const eventProperties = getTransformedEventProperties({
        trackingAttributes: getAllAttributes(),
      });

      return {
        name: FlightsPriceFreezePurchaseTrackingEvents.PRICE_FREEZE_DURATION_CHANGED,
        properties: {
          ...eventProperties.flightPriceFreezeProperties,
          ...event.properties,
        },
      };
    },
    [getAllAttributes]
  );
};

const useFlightsPriceFreezePaymentLoadedTransformer: UseInstantTransformEvent<
  FlightsPriceFreezeRawEvent,
  FlightsPriceFreezeHTSEvent
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(
    (event) => {
      const eventProperties = getTransformedEventProperties({
        trackingAttributes: getAllAttributes(),
      });

      return {
        name: FlightsPriceFreezePurchaseTrackingEvents.PRICE_FREEZE_VIEWED_PAYMENT_SELECTION,
        properties: {
          ...eventProperties.flightPriceFreezeProperties,
          ...event.properties,
        },
      };
    },
    [getAllAttributes]
  );
};

const useFlightsPriceFreezePurchaseClickedTransformer: UseInstantTransformEvent<
  FlightsPriceFreezeRawEvent,
  FlightsPriceFreezeHTSEvent
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(
    (event) => {
      const eventProperties = getTransformedEventProperties({
        trackingAttributes: getAllAttributes(),
      });

      return {
        name: FlightsPriceFreezePurchaseTrackingEvents.PRICE_FREEZE_PURCHASE_SELECTED,
        properties: {
          ...eventProperties.flightPriceFreezeProperties,
          ...event.properties,
        },
      };
    },
    [getAllAttributes]
  );
};

const useFlightsPriceFreezePurchaseConfirmationTransformer: UseInstantTransformEvent<
  FlightsPriceFreezeRawEvent,
  FlightsPriceFreezeHTSEvent
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(
    (event) => {
      const eventProperties = getTransformedEventProperties({
        trackingAttributes: getAllAttributes(),
      });

      return {
        name: FlightsPriceFreezePurchaseTrackingEvents.PRICE_FREEZE_VIEWED_CONFIRMATION_SCREEN,
        properties: {
          ...eventProperties.flightPriceFreezeProperties,
          ...event.properties,
        },
      };
    },
    [getAllAttributes]
  );
};

export const FLIGHTS_PRICE_FREEZE_PURCHASE_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  FlightsPriceFreezeRawEvent,
  FlightsPriceFreezeHTSEvent
> = {
  [FlightsPriceFreezePurchaseRawEventName.FlightsPriceFreezeEntryLoaded]: [
    useFlightsPriceFreezeEntryLoadedTransformer,
  ],
  [FlightsPriceFreezePurchaseRawEventName.FlightsPriceFreezeEntryClicked]: [
    useFlightsPriceFreezeEntryClickedTransformer,
  ],
  [FlightsPriceFreezePurchaseRawEventName.FlightsPriceFreezeDetailsLoaded]: [
    useFlightsPriceFreezeDetailsLoadedTransformer,
  ],
  [FlightsPriceFreezePurchaseRawEventName.FlightsPriceFreezeDurationChanged]: [
    useFlightsPriceFreezeDurationChangedTransformer,
  ],
  [FlightsPriceFreezePurchaseRawEventName.FlightsPriceFreezePaymentLoaded]: [
    useFlightsPriceFreezePaymentLoadedTransformer,
  ],
  [FlightsPriceFreezePurchaseRawEventName.FlightsPriceFreezePurchaseClicked]: [
    useFlightsPriceFreezePurchaseClickedTransformer,
  ],
  [FlightsPriceFreezePurchaseRawEventName.FlightsPriceFreezePurchaseConfirmation]:
    [useFlightsPriceFreezePurchaseConfirmationTransformer],
};
